/**
 * <cba-dialog> — every drawer, bottom sheet and popup (brand/identity/05
 * "Drawers, sheets and popups"; DESIGN-RULES §6; THEME-STANDARD §6.6).
 * The look is .cba-dialog in snippets/cba-classes.liquid.
 *
 *   <cba-dialog name="care">
 *     <dialog class="cba-dialog cba-dialog--side" aria-labelledby="care-title">
 *       <header class="cba-dialog__head">
 *         <div class="cba-dialog__top">
 *           <h2 class="cba-dialog__title cba-type-dialog-title" id="care-title">How to care</h2>
 *           <button type="button" class="cba-close-btn" data-cba-dialog-close aria-label="Close care guide">…</button>
 *         </div>
 *       </header>
 *       <div class="cba-dialog__body">…</div>
 *     </dialog>
 *   </cba-dialog>
 *
 * Open it from a trigger anywhere on the page,
 *   <button type="button" data-cba-dialog-open="care" aria-expanded="false">…</button>
 * or from a component script: document.querySelector('cba-dialog[name="care"]').open(trigger).
 *
 * - showModal(): the page behind is inert and dimmed (::backdrop), focus
 *   moves to the first control in the dialog, and closing returns focus to
 *   the element that had it; when that element is gone, to the X of the
 *   overlay underneath.
 * - It slides in and out with its type's transition (none with reduced
 *   motion). Escape, the X and a click on the backdrop close it.
 * - One overlay at a time: opening announces `cba:overlay:open` with its
 *   name, and it closes at once when another overlay announces. With the
 *   `stack` attribute it opens over another one without announcing (the
 *   quick-add sheet over the cart, the gift popup over the scratch popup).
 * - `media="(max-width: 1023px)"`: it only exists under that query and
 *   closes when the viewport leaves it (the mobile menu).
 * - Scroll lock: html[data-cba-drawer-open] while any dialog is open.
 * - The trigger's aria-expanded, when it has one, follows the state.
 * - Events on the element, bubbling: `cba-dialog:open` (detail.trigger)
 *   and `cba-dialog:close`, for the component's own logic.
 * - A popup (.cba-dialog--popup) is a transparent full-screen frame that
 *   centres its card: a click on the frame, outside the card, closes it.
 */
(() => {
  if (customElements.get('cba-dialog')) return;

  const root = document.documentElement;
  const opened = new Set();
  let ownsLock = false;

  /* The search panel locks the scroll the same way. A dialog stacked over
     it (quick add from a search result) must not release that lock, so the
     dialogs only remove a lock they set themselves. */
  function syncScrollLock() {
    if (opened.size) {
      if (!root.hasAttribute('data-cba-drawer-open')) {
        root.setAttribute('data-cba-drawer-open', '1');
        ownsLock = true;
      }
    } else if (ownsLock) {
      root.removeAttribute('data-cba-drawer-open');
      ownsLock = false;
    }
  }

  /* The longest transition on the element, in ms: how long a close slides. */
  function transitionMs(el) {
    const style = getComputedStyle(el);
    const times = (value) => value.split(',').map((t) => parseFloat(t) * (/ms\s*$/.test(t) ? 1 : 1000) || 0);
    const durations = times(style.transitionDuration);
    const delays = times(style.transitionDelay);
    return Math.max(0, ...durations.map((d, i) => d + (delays[i % delays.length] || 0)));
  }

  class CbaDialog extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector(':scope > dialog');
      if (!this.dialog) return;

      this.onCancel = (event) => {
        event.preventDefault();
        this.close();
      };
      this.onClick = (event) => {
        const closer = event.target.closest('[data-cba-dialog-close]');
        if (closer && closer.closest('cba-dialog') === this) {
          this.close();
          return;
        }
        if (event.target !== this.dialog) return;
        /* A popup's <dialog> is a transparent frame around its card: a click
           on the frame closes it. */
        if (this.dialog.classList.contains('cba-dialog--popup')) {
          this.close();
          return;
        }
        /* A click on the ::backdrop targets the dialog itself, outside its box. */
        const r = this.dialog.getBoundingClientRect();
        if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.close();
      };
      this.onOverlay = (event) => {
        if (event.detail !== this.name) this.closeNow();
      };
      this.dialog.addEventListener('cancel', this.onCancel);
      this.dialog.addEventListener('click', this.onClick);
      document.addEventListener('cba:overlay:open', this.onOverlay);

      const media = this.getAttribute('media');
      if (media) {
        this.mq = window.matchMedia(media);
        this.onMedia = () => {
          if (!this.mq.matches) this.closeNow();
        };
        this.mq.addEventListener('change', this.onMedia);
      }
    }

    disconnectedCallback() {
      if (!this.dialog) return;
      this.dialog.removeEventListener('cancel', this.onCancel);
      this.dialog.removeEventListener('click', this.onClick);
      document.removeEventListener('cba:overlay:open', this.onOverlay);
      if (this.mq) this.mq.removeEventListener('change', this.onMedia);
      clearTimeout(this.timer);
      opened.delete(this);
      syncScrollLock();
    }

    get name() {
      return this.getAttribute('name') || '';
    }

    /** True while it is open and not sliding out. */
    get isOpen() {
      return Boolean(this.dialog && this.dialog.open && !this.closing);
    }

    /** Opens it; on an open dialog it only cancels a close and updates the trigger. */
    open(trigger) {
      const dialog = this.dialog;
      if (!dialog) return;
      clearTimeout(this.timer);
      this.closing = false;
      if (!(trigger instanceof Element)) trigger = null;
      if (trigger) {
        if (this.trigger && this.trigger !== trigger) this.setExpanded(false);
        this.trigger = trigger;
      }
      if (!dialog.open) {
        if (!this.hasAttribute('stack')) {
          document.dispatchEvent(new CustomEvent('cba:overlay:open', { detail: this.name }));
        }
        dialog.showModal();
        /* Commit the closed position first, so the class slides it in. */
        dialog.getBoundingClientRect();
      }
      dialog.classList.add('is-open');
      opened.add(this);
      syncScrollLock();
      this.setExpanded(true);
      this.dispatchEvent(new CustomEvent('cba-dialog:open', { bubbles: true, detail: { trigger } }));
    }

    /** Slides out, then closes; close() returns focus to the trigger. */
    close() {
      const dialog = this.dialog;
      if (!dialog || !dialog.open || this.closing) return;
      this.closing = true;
      dialog.classList.remove('is-open');
      const ms = transitionMs(dialog);
      if (ms) this.timer = setTimeout(() => this.finish(), ms);
      else this.finish();
    }

    /** Closes without the slide: another overlay is taking its place. */
    closeNow() {
      const dialog = this.dialog;
      if (!dialog || !dialog.open) return;
      clearTimeout(this.timer);
      dialog.classList.remove('is-open');
      this.finish();
    }

    finish() {
      this.closing = false;
      if (this.dialog.open) this.dialog.close();
      opened.delete(this);
      /* The element that had focus can be gone: an add from the quick-add
         sheet over the cart re-renders the row its Add control was in.
         Focus is then left in the closed dialog, or nowhere; it goes to the
         overlay underneath, where opening it puts it (its X). */
      const active = document.activeElement;
      if (!active || active === document.body || this.dialog.contains(active)) {
        const under = [...opened].pop();
        const control = under && under.dialog && under.dialog.querySelector('[data-cba-dialog-close]');
        if (control) control.focus({ preventScroll: true });
      }
      syncScrollLock();
      this.setExpanded(false);
      this.trigger = null;
      this.dispatchEvent(new CustomEvent('cba-dialog:close', { bubbles: true }));
    }

    setExpanded(isOpen) {
      if (this.trigger && this.trigger.hasAttribute('aria-expanded')) {
        this.trigger.setAttribute('aria-expanded', String(isOpen));
      }
    }
  }

  customElements.define('cba-dialog', CbaDialog);

  /* Any [data-cba-dialog-open="name"] opens the <cba-dialog name="name">. */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-cba-dialog-open]');
    if (!trigger) return;
    const dialog = document.querySelector(`cba-dialog[name="${CSS.escape(trigger.dataset.cbaDialogOpen)}"]`);
    if (!dialog || !dialog.dialog) return;
    event.preventDefault();
    dialog.open(trigger);
  });
})();
